/**
 * Repère de progression : la frise de campagne, un cran par chapitre, groupés par mission.
 *
 * Pas de pourcentage : les chapitres n'ont pas la même longueur, un cran dit ce que le
 * joueur a vraiment fait. La frise se construit depuis `metadata.missions` et
 * `metadata.chapters`, rien n'est écrit en dur.
 */

import type { Mission, Scenario } from '../types';
import { buildInlineStructure } from './inline-scene-model';
import { getMissionStartScene } from './mission-progress';
import { findChapterContainingScene } from './scene-flow';
import { resolveTextOverlayType } from './text-overlay-content';

export type FriseCranState = 'fait' | 'ici' | 'remplit' | 'avenir';
export type FriseMissionState = 'passee' | 'cours' | 'avenir';

export interface FriseCran {
  chapterId: string;
  state: FriseCranState;
}

export interface FriseMission {
  id: string;
  /** « M3 » */
  label: string;
  state: FriseMissionState;
  crans: FriseCran[];
}

export interface CampaignFrise {
  missions: FriseMission[];
  currentMissionId: string;
  currentChapterId: string;
  currentChapterTitle: string;
  totalChapters: number;
  doneChapters: number;
  /** Rang de la mission courante dans la campagne (1-based). */
  missionPosition: number;
  missionCount: number;
  /** Rang du chapitre courant dans sa mission (1-based). */
  chapterPosition: number;
  chapterCount: number;
  /** Chapitres qui suivent le chapitre courant dans la mission. */
  remainingInMission: number;
}

export type CampaignFriseMode = 'opening' | 'chapterEnd';

export interface BuildCampaignFriseOptions {
  sceneId: string;
  mode: CampaignFriseMode;
  completedMissionIds?: readonly string[];
}

/**
 * - Fait : tous les chapitres des missions terminées (hors mission courante), plus,
 *   dans la mission courante, les chapitres qui précèdent le chapitre courant.
 * - Le chapitre courant est `ici` à l'ouverture, `remplit` en fin de chapitre (et compte
 *   alors comme fait).
 */
export function buildCampaignFrise(
  scenario: Scenario,
  { sceneId, mode, completedMissionIds = [] }: BuildCampaignFriseOptions,
): CampaignFrise | null {
  const groups = buildInlineStructure(scenario).filter(
    (group): group is typeof group & { mission: Mission } =>
      group.mission !== null && group.chapters.some((entry) => entry.chapter !== null),
  );
  if (groups.length === 0) return null;

  const currentChapter = findChapterContainingScene(scenario, sceneId);
  if (!currentChapter) return null;

  const currentMissionIndex = groups.findIndex((group) =>
    group.chapters.some((entry) => entry.chapter?.id === currentChapter.id),
  );
  if (currentMissionIndex < 0) return null;

  const completed = new Set(completedMissionIds);
  let totalChapters = 0;
  let doneChapters = 0;
  let chapterPosition = 0;
  let chapterCount = 0;

  const missions = groups.map((group, missionIndex): FriseMission => {
    const isCurrent = missionIndex === currentMissionIndex;
    const isCompleted = !isCurrent && completed.has(group.mission.id);
    const chapters = group.chapters.flatMap((entry) => (entry.chapter ? [entry.chapter] : []));
    const currentIndex = isCurrent
      ? chapters.findIndex((chapter) => chapter.id === currentChapter.id)
      : -1;

    if (isCurrent) {
      chapterPosition = currentIndex + 1;
      chapterCount = chapters.length;
    }

    const crans = chapters.map((chapter, chapterIndex): FriseCran => {
      let state: FriseCranState = 'avenir';
      if (isCompleted || (isCurrent && chapterIndex < currentIndex)) state = 'fait';
      if (isCurrent && chapterIndex === currentIndex) {
        state = mode === 'chapterEnd' ? 'remplit' : 'ici';
      }
      totalChapters += 1;
      if (state === 'fait' || state === 'remplit') doneChapters += 1;
      return { chapterId: chapter.id, state };
    });

    return {
      id: group.mission.id,
      label: `M${missionIndex + 1}`,
      state: isCurrent ? 'cours' : isCompleted ? 'passee' : 'avenir',
      crans,
    };
  });

  return {
    missions,
    currentMissionId: groups[currentMissionIndex]!.mission.id,
    currentChapterId: currentChapter.id,
    currentChapterTitle: stripChapterPrefix(currentChapter.title),
    totalChapters,
    doneChapters,
    missionPosition: currentMissionIndex + 1,
    missionCount: groups.length,
    chapterPosition,
    chapterCount,
    remainingInMission: Math.max(0, chapterCount - chapterPosition),
  };
}

function stripPrefix(title: string, word: string): string {
  const pattern = new RegExp(`^\\s*${word}\\s*\\d+\\s*[·:.\\-–—]\\s*`, 'i');
  const stripped = title.replace(pattern, '').trim();
  return stripped || title.trim();
}

/** « Chapitre 2 · Le flux » → « Le flux ». */
export function stripChapterPrefix(title: string): string {
  return stripPrefix(title, 'chapitre');
}

/** « Mission 4 : Les secrets voyagent mal » → « Les secrets voyagent mal ». */
export function stripMissionPrefix(title: string): string {
  return stripPrefix(title, 'mission');
}

const NOMBRES = ['zéro', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix'];

function enLettres(n: number): string {
  return NOMBRES[n] ?? String(n);
}

export function formatRemainingInMission(frise: CampaignFrise): string {
  const remaining = frise.remainingInMission;
  if (remaining === 1) return 'Il reste un chapitre dans cette mission.';
  if (remaining > 1) return `Il reste ${enLettres(remaining)} chapitres dans cette mission.`;
  if (frise.missionPosition < frise.missionCount) {
    return `Mission terminée. La mission ${frise.missionPosition + 1} vous attend.`;
  }
  return 'Mission terminée. La campagne est terminée.';
}

export function formatDoneCount(frise: CampaignFrise): string {
  const word = frise.doneChapters > 1 ? 'chapitres faits' : 'chapitre fait';
  return `${frise.doneChapters} ${word} sur ${frise.totalChapters}`;
}

export function describeCampaignFrise(frise: CampaignFrise): string {
  return `${formatDoneCount(frise)}, mission ${frise.missionPosition} en cours`;
}

/** Mission dont le premier chapitre commence sur cette scène. */
export function findMissionStartingAt(scenario: Scenario, sceneId: string): Mission | undefined {
  if (!sceneId) return undefined;
  return (scenario.metadata.missions ?? []).find(
    (mission) => getMissionStartScene(scenario, mission.id) === sceneId,
  );
}

/**
 * Ouverture générée : la mission commence sur une scène qui n'est pas une lettre
 * (ex. mission 4, qui démarre sur un dialogue).
 */
export function resolveGeneratedMissionOpening(
  scenario: Scenario,
  sceneId: string,
): { missionId: string; title: string } | null {
  const mission = findMissionStartingAt(scenario, sceneId);
  if (!mission) return null;
  const scene = scenario.scenes.find((entry) => entry.id === sceneId);
  if (!scene) return null;
  if (scene.type === 'chapterEnd') return null;
  if (
    scene.type === 'textOverlay'
    && scene.overlayType !== 'chapterEnd'
    && resolveTextOverlayType(scene.overlayType) === 'letter'
  ) {
    return null;
  }
  return { missionId: mission.id, title: stripMissionPrefix(mission.title) };
}
